import { filterByTextListWithUnknownPayload } from "@/domain/view/components/datatable/datatable-filters/functions/filter-by-text-list";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow } from "@/domain/view/components/datatable/datatable-types";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { useUpdateIndicator } from "../../../hooks/api/use-update-indicator";
import { IndicatorListItem } from "../../../models/indicator-model";
import { sortIndicatorType } from "../../../models/indicator-type-model";
import { DatatableIndicatorTypeAppliedFilterView } from "../cells/datatable-indicator-type-applied-filter-view";
import { DatatableIndicatorTypeCell } from "../cells/datatable-indicator-type-cell";
import { DatatableIndicatorTypeConfigurationView } from "../cells/datatable-indicator-type-configuration-view";
import { DatatableIndicatorTypeDisplayView } from "../cells/datatable-indicator-type-display-view";
import { DatatableIndicatorTypeSectionHeader } from "../cells/datatable-indicator-type-section-header";
import { DatatableIndicatorTypeSubSectionHeader } from "../cells/datatable-indicator-type-subsection-header";

export const COLUMN_ID_INDICATOR_TYPE = "indicator_type";
export const COLUMN_ORDER_VALUE_INDICATOR_TYPE = 105;
export const COLUMN_ID_INDICATOR_TYPE_DISPLAY_VALUE = "Kategorie";

// local component to be able to call hook
const IndicatorTypeDatatableCell = ({
  type,
  indicator,
  editable,
}: {
  type: string | null | undefined;
  indicator: IndicatorListItem;
  editable: boolean;
}) => {
  const { updateIndicator } = useUpdateIndicator();

  const handleOnSelect = (type: string | null) => {
    void updateIndicator({
      variables: {
        input: {
          id: indicator.id,
          type: type ?? null,
        },
      },
    });
  };

  return <DatatableIndicatorTypeCell indicatorType={type} editable={editable} onSelect={handleOnSelect} />;
};

interface IndicatorTypeColumnProps {
  workspace: Workspace;
  columnId?: string;
  orderValue?: number;
  displayValue?: string;
  editable?: boolean;
}

export const IndicatorTypeColumn = ({
  columnId = COLUMN_ID_INDICATOR_TYPE,
  orderValue = COLUMN_ORDER_VALUE_INDICATOR_TYPE,
  displayValue = COLUMN_ID_INDICATOR_TYPE_DISPLAY_VALUE,
  editable = false,
}: IndicatorTypeColumnProps): DatatableColumn<IndicatorListItem> => {
  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue, // prefix with 100, 200, 300 to assign to sections
    enableSectioning: true,
    getSectionKey: (data) => data.type ?? "",
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId, // key needed to enable sorting / filtering
        enableHiding: true,
        enableSorting: true,
        enableColumnFilter: true,
        size: 1, // set to 1 instead to 0 for it to work in Safari
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          return sortIndicatorType(rowA.original.type, rowB.original.type);
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            return true;
          }

          return filterByTextListWithUnknownPayload(value, row.original.type);
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className={`text-xs font-normal`}>
            {displayValue}
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          return <IndicatorTypeDatatableCell type={row.original.type} indicator={row.original} editable={editable} />;
        },
      };
    },
    renderDisplayView: () => <DatatableIndicatorTypeDisplayView />,
    getFilterConfigurationViewType: () => "popover",
    renderFilterConfigurationView: (onFinished: () => void) => (
      <DatatableIndicatorTypeConfigurationView column={{ id: columnId }} onFinished={onFinished} />
    ),
    renderAppliedFilterView: () => <DatatableIndicatorTypeAppliedFilterView column={{ id: columnId }} />,
    renderSectionHeader: (data, row) => {
      return <DatatableIndicatorTypeSectionHeader data={data} currentRow={row} />;
    },
    renderSubSectionHeader: (data, row) => {
      return <DatatableIndicatorTypeSubSectionHeader data={data} currentRow={row} />;
    },
  };
};
